import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { chartsListRoute } from "app/router";
import {
    Button,
    CardTitle,
    Table as T,
    TablePagination,
    TableSearch,
    TableView,
} from "components/elements";
import { FavoriteButton } from "components/elements/FavoriteButton";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useThrottled } from "hooks/useThrottled";
import { useUserTimezone } from "hooks/useUserTimezone";
import { trpc } from "lib/trpc";

export const ChartsListScreen = () => {
    const input = useSearch({ from: chartsListRoute.id });
    const navigate = useNavigate();
    const timeZone = useUserTimezone();
    const currentUser = useCurrentUser();
    const isCompassUser = currentUser?.isCompassUser ?? false;
    const throttledNavigate = useThrottled(navigate);
    const [searchValue, setSearchValue] = useState<string>(input.search ?? "");

    const { data, isPending: isLoading } = trpc.charts.list.useQuery({
        page: input.page ?? 0,
        pageSize: input.pageSize ?? 10,
        search: input.search,
    });

    const handleSearch = (value: string) => {
        setSearchValue(value);
        throttledNavigate({
            search: { ...input, search: value || undefined, page: 0 },
        });
    };

    const handlePageChange = (page: number | undefined) => {
        navigate({ search: { ...input, page } });
    };

    return (
        <div className="pt-6">
            <div className="mb-6 flex items-center justify-between">
                <CardTitle>Visualizations</CardTitle>
                <Button onClick={() => navigate({ to: "/visualizations/new" })}>Create</Button>
            </div>

            <div className="mb-3">
                <TableSearch
                    value={searchValue}
                    onChange={(value: string) => handleSearch(value)}
                />
            </div>

            <TableView
                data={data}
                isInitialLoading={isLoading}
                isFiltering={!!input.search}
                emptyCollection={
                    <div>
                        No visualizations yet. Create your first visualization to get started.
                    </div>
                }
                footer={
                    <TablePagination
                        total={data?.total}
                        page={input.page}
                        pageSize={input.pageSize}
                        onChangePage={handlePageChange}
                    />
                }
            >
                {(data) => (
                    <T.Table>
                        <T.Head>
                            <T.HeadRow>
                                <T.Cell>Title</T.Cell>
                                <T.Cell>Author</T.Cell>
                                {isCompassUser && <T.Cell>Organization</T.Cell>}
                                <T.Cell>Versions</T.Cell>
                                <T.Cell>Permission</T.Cell>
                                <T.Cell>Last Updated</T.Cell>
                                <T.Cell>AI Summary</T.Cell>
                                <T.Cell></T.Cell>
                            </T.HeadRow>
                        </T.Head>
                        <T.Body>
                            {data.charts.map((chart) => (
                                <T.Row
                                    key={chart.id}
                                    className="cursor-pointer hover:bg-gray-50"
                                    onClick={() =>
                                        navigate({
                                            to: "/visualizations/$id",
                                            params: { id: String(chart.id) },
                                        })
                                    }
                                >
                                    <T.Cell>
                                        <div>
                                            <div className="font-medium">{chart.title}</div>
                                            {chart.description && (
                                                <div className="max-w-xs truncate text-sm text-gray-500">
                                                    {chart.description}
                                                </div>
                                            )}
                                        </div>
                                    </T.Cell>
                                    <T.Cell>{chart.author.name}</T.Cell>
                                    {isCompassUser && (
                                        <T.Cell>{chart.authorOrganization?.name ?? "-"}</T.Cell>
                                    )}
                                    <T.Cell>{chart.versionCount}</T.Cell>
                                    <T.Cell>
                                        <span
                                            style={{
                                                padding: "2px 8px",
                                                borderRadius: 4,
                                                fontSize: 12,
                                                fontWeight: 500,
                                                background:
                                                    chart.currentUserPermission === "OWNER"
                                                        ? "#dcfce7"
                                                        : chart.currentUserPermission === "EDITOR"
                                                          ? "#dbeafe"
                                                          : "#f3f4f6",
                                                color:
                                                    chart.currentUserPermission === "OWNER"
                                                        ? "#166534"
                                                        : chart.currentUserPermission === "EDITOR"
                                                          ? "#2563eb"
                                                          : "#6b7280",
                                            }}
                                        >
                                            {chart.currentUserPermission}
                                        </span>
                                    </T.Cell>
                                    <T.Cell>
                                        {new Date(chart.updatedAt).toLocaleDateString(undefined, {
                                            timeZone,
                                        })}
                                    </T.Cell>
                                    <T.Cell className="w-48 max-w-xs">
                                        {chart?.activeVersion?.aiSummary ? (
                                            <>
                                                <div className="font-medium">
                                                    {chart.activeVersion.aiSummary.title}
                                                </div>
                                                <div className="max-w-xs truncate text-sm text-gray-500">
                                                    {chart.activeVersion.aiSummary.summary}
                                                </div>
                                            </>
                                        ) : (
                                            <span className="text-sm text-gray-400">-</span>
                                        )}
                                    </T.Cell>
                                    <T.Cell>
                                        <FavoriteButton
                                            target={{ type: "chart", id: chart.id }}
                                            size={16}
                                        />
                                    </T.Cell>
                                </T.Row>
                            ))}
                        </T.Body>
                    </T.Table>
                )}
            </TableView>
        </div>
    );
};
